import { useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  Package,
  FileText,
  ShieldCheck,
  Wrench,
  CheckSquare,
  ClipboardCheck,
  Building2,
  CalendarDays,
  Bell,
  ScrollText,
  Settings2,
  X,
  ChevronsLeft,
  ChevronsRight,
  BookOpen,
  GraduationCap,
  ShieldAlert,
  GitPullRequest,
  AlertTriangle,
  BarChart3,
} from 'lucide-react';
import { usePermissions } from '@/hooks/useAuth';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  roles?: string[]; // If undefined, visible to all roles
}

const mainNavItems: NavItem[] = [
  { label: 'Dashboard', path: '/', icon: <LayoutDashboard size={20} /> },
  { label: 'Assets', path: '/assets', icon: <Package size={20} /> },
  { label: 'Documents', path: '/documents', icon: <FileText size={20} /> },
  { label: 'Compliance', path: '/compliance', icon: <ShieldCheck size={20} /> },
  { label: 'AMC', path: '/amc', icon: <Wrench size={20} /> },
  { label: 'Tasks', path: '/tasks', icon: <CheckSquare size={20} /> },
  { label: 'Approvals', path: '/approvals', icon: <ClipboardCheck size={20} /> },
  {
    label: 'Vendors',
    path: '/vendors',
    icon: <Building2 size={20} />,
  },
  { label: 'Calendar', path: '/calendar', icon: <CalendarDays size={20} /> },
];

const qmsNavItems: NavItem[] = [
  { label: 'SOPs', path: '/sops', icon: <BookOpen size={20} /> },
  { label: 'Training', path: '/training', icon: <GraduationCap size={20} /> },
  { label: 'CAPA', path: '/capa', icon: <ShieldAlert size={20} /> },
  { label: 'Change Control', path: '/change-control', icon: <GitPullRequest size={20} /> },
  { label: 'Risk Management', path: '/risk-management', icon: <AlertTriangle size={20} /> },
  { label: 'QMS Dashboard', path: '/qms-dashboard', icon: <BarChart3 size={20} /> },
];

const systemNavItems: NavItem[] = [
  { label: 'Notifications', path: '/notifications', icon: <Bell size={20} /> },
  {
    label: 'Audit Logs',
    path: '/audit-logs',
    icon: <ScrollText size={20} />,
  },
  {
    label: 'Settings',
    path: '/settings',
    icon: <Settings2 size={20} />,
  },
];

function NavLink({ item, isActive, collapsed }: { item: NavItem; isActive: boolean; collapsed?: boolean }) {
  return (
    <Link
      to={item.path}
      className={`relative flex items-center ${collapsed ? 'justify-center' : 'gap-3'} rounded-lg ${collapsed ? 'px-2 py-2.5' : 'px-3 py-2.5'} text-sm font-medium transition-all duration-200 ${
        isActive
          ? 'bg-indigo-500/10 text-white'
          : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
      }`}
      title={collapsed ? item.label : undefined}
    >
      {/* Active indicator bar */}
      {isActive && (
        <motion.div
          layoutId="sidebar-active"
          className="absolute left-0 top-1 bottom-1 w-0.5 rounded-full bg-indigo-500"
          transition={{ type: 'spring', stiffness: 350, damping: 30 }}
        />
      )}
      <span className={isActive ? 'text-indigo-400' : ''}>{item.icon}</span>
      {!collapsed && <span>{item.label}</span>}
    </Link>
  );
}

export default function Sidebar({ isOpen, onClose, collapsed = false, onToggleCollapse }: SidebarProps) {
  const location = useLocation();
  const { role } = usePermissions();

  const isActiveRoute = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const filterByRole = (items: NavItem[]) =>
    items.filter((item) => {
      if (!item.roles) return true;
      return role ? item.roles.includes(role) : false;
    });

  const visibleMainItems = filterByRole(mainNavItems);
  const visibleQmsItems = filterByRole(qmsNavItems);
  const visibleSystemItems = filterByRole(systemNavItems);

  const sidebarContent = (
    <nav className="flex flex-1 flex-col gap-1 px-3">
      {/* Main section */}
      {!collapsed && (
        <p className="mb-1 mt-4 px-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
          Main
        </p>
      )}
      {collapsed && <div className="mt-4" />}
      {visibleMainItems.map((item) => (
        <NavLink key={item.path} item={item} isActive={isActiveRoute(item.path)} collapsed={collapsed} />
      ))}

      {/* QMS section */}
      {visibleQmsItems.length > 0 && (
        <>
          {!collapsed && (
            <p className="mb-1 mt-6 px-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
              Quality Management
            </p>
          )}
          {collapsed && <div className="mt-6" />}
          {visibleQmsItems.map((item) => (
            <NavLink key={item.path} item={item} isActive={isActiveRoute(item.path)} collapsed={collapsed} />
          ))}
        </>
      )}

      {/* System section */}
      {visibleSystemItems.length > 0 && (
        <>
          {!collapsed && (
            <p className="mb-1 mt-6 px-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
              System
            </p>
          )}
          {collapsed && <div className="mt-6" />}
          {visibleSystemItems.map((item) => (
            <NavLink key={item.path} item={item} isActive={isActiveRoute(item.path)} collapsed={collapsed} />
          ))}
        </>
      )}
    </nav>
  );

  return (
    <>
      {/* Desktop sidebar — always visible */}
      <aside className={`hidden ${collapsed ? 'w-16' : 'w-64'} flex-shrink-0 flex-col bg-slate-900/95 backdrop-blur-xl lg:flex transition-all duration-300`}>
        {/* Logo + Collapse toggle */}
        <div className="flex h-16 items-center justify-between px-4">
          <Link to="/" className="flex items-center gap-2">
            <span className="bg-gradient-to-r from-indigo-400 to-indigo-600 bg-clip-text text-xl font-bold text-transparent">
              {collapsed ? 'T' : 'TrackDox'}
            </span>
          </Link>
          {onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
            </button>
          )}
        </div>
        {sidebarContent}
      </aside>

      {/* Mobile sidebar — slide in/out overlay */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-40 bg-black/50 lg:hidden"
              onClick={onClose}
            />

            {/* Drawer */}
            <motion.aside
              initial={{ x: -256 }}
              animate={{ x: 0 }}
              exit={{ x: -256 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="fixed inset-y-0 left-0 z-50 flex w-64 flex-col bg-slate-900/95 backdrop-blur-xl lg:hidden"
            >
              {/* Logo + close button */}
              <div className="flex h-16 items-center justify-between px-6">
                <Link to="/" className="flex items-center gap-2" onClick={onClose}>
                  <span className="bg-gradient-to-r from-indigo-400 to-indigo-600 bg-clip-text text-xl font-bold text-transparent">
                    TrackDox
                  </span>
                </Link>
                <button
                  onClick={onClose}
                  className="rounded-lg p-1.5 text-slate-400 hover:bg-white/5 hover:text-white transition-colors"
                  aria-label="Close sidebar"
                >
                  <X size={20} />
                </button>
              </div>
              {sidebarContent}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
